import React from 'react';
import { X, Play, Pause, Volume2, ExternalLink, ChevronUp, Radio, Clock, Loader2 } from 'lucide-react';
import { useRadioPlayer, resolveRadioImg } from '../radio';

const RADIO_LOGO = 'https://customer-assets-v7afamib.emergentagent.net/job_yup-rebuild/artifacts/4ahgeufv_IMG_3203.png';
const RADIO_WEBSITE = 'https://www.studiopiu.net/studiopiu/';

// ---------- MINI PLAYER (bottom-right sticky) ----------
export function RadioMiniPlayer() {
  const { now, playing, toggle, miniVisible, openModal, closeMini } = useRadioPlayer();
  if (!miniVisible) return null;

  return (
    <div
      role="region" aria-label="Mini player Radio Studio Più"
      data-testid="radio-mini-player"
      className="fixed bottom-4 right-4 z-[70] w-72 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-red-500/40 bg-gradient-to-br from-red-950/95 to-black/95 backdrop-blur-md">
      <div className="flex items-center gap-2 px-3 pt-2 text-[10px] text-red-300 uppercase tracking-wider font-bold">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"/> LIVE · Radio Studio Più
        <button onClick={closeMini} className="ml-auto text-white/50 hover:text-white p-0.5" aria-label="Chiudi mini player" data-testid="mini-close">
          <X size={13}/>
        </button>
      </div>
      <div className="flex items-center gap-3 px-3 pb-3 pt-1">
        <button onClick={toggle} data-testid="mini-play-toggle"
                className="w-11 h-11 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center flex-shrink-0 transition transform active:scale-95">
          {playing ? <Pause size={18} fill="white" className="text-white"/> : <Play size={18} fill="white" className="text-white ml-0.5"/>}
        </button>
        <button onClick={openModal} className="flex-1 min-w-0 text-left" data-testid="mini-expand">
          <div className="text-[13px] font-semibold text-white truncate">
            {now?.program?.title || 'In diretta'}
          </div>
          <div className="text-[10px] text-white/60 truncate">
            {now?.program?.staff || 'Radio Studio Più'}
          </div>
        </button>
        <button onClick={openModal} className="text-white/60 hover:text-white p-1" aria-label="Espandi" data-testid="mini-expand-btn">
          <ChevronUp size={18}/>
        </button>
      </div>
    </div>
  );
}


// ---------- FULL MODAL PLAYER (with schedule) ----------
export function RadioFullModal() {
  const { now, schedule, playing, volume, modalOpen, error,
          toggle, setVolume, closeModal } = useRadioPlayer();
  if (!modalOpen) return null;

  const nowImage = resolveRadioImg(now?.program?.image);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
         onClick={closeModal} data-testid="radio-modal">
      <div className="w-full max-w-lg bg-gradient-to-br from-[#0a0a0a] via-red-950/50 to-black rounded-2xl border border-red-500/40 overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/60 sticky top-0 z-10">
          <div className="flex items-center gap-2 text-white text-sm font-semibold">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
            <span className="uppercase tracking-wider">In diretta</span>
          </div>
          <button onClick={closeModal}
                  data-testid="radio-modal-close"
                  className="text-white/80 hover:text-white bg-white/10 hover:bg-red-500/40 rounded-full w-11 h-11 flex items-center justify-center transition -mr-1"
                  aria-label="Chiudi player">
            <X size={22}/>
          </button>
        </div>

        {/* Now playing */}
        <div className="p-6 flex flex-col items-center text-center">
          <div className="relative mb-4">
            {nowImage ? (
              <img src={nowImage} alt={now?.program?.title || 'Radio'}
                   onError={(e) => { e.target.src = RADIO_LOGO; }}
                   className={`w-32 h-32 rounded-2xl object-cover shadow-[0_10px_30px_rgba(239,68,68,0.35)] ${playing ? 'animate-radio-beat' : ''}`}/>
            ) : (
              <img src={RADIO_LOGO} alt="Radio Studio Più"
                   className={`w-32 h-32 rounded-2xl object-cover shadow-lg ${playing ? 'animate-radio-beat' : ''}`}/>
            )}
            {playing && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-0.5 items-end h-6">
                <span className="w-1 bg-[color:var(--yup-primary)] animate-bar1"/>
                <span className="w-1 bg-[color:var(--yup-primary)] animate-bar2"/>
                <span className="w-1 bg-[color:var(--yup-primary)] animate-bar3"/>
                <span className="w-1 bg-[color:var(--yup-primary)] animate-bar4"/>
              </div>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1">Ora in onda</div>
          <h3 className="text-xl font-bold text-white" data-testid="radio-current-title">
            {now?.program?.title || 'Caricamento...'}
          </h3>
          {now?.program?.staff && (
            <p className="text-xs text-white/70 mt-1 whitespace-pre-line">{now.program.staff}</p>
          )}
          {now?.program?.start && (
            <p className="text-[11px] text-white/40 mt-1 inline-flex items-center gap-1">
              <Clock size={11}/> {String(now.program.start).slice(0,5)} — {String(now.program.end).slice(0,5)}
            </p>
          )}

          {/* Play button */}
          <button onClick={toggle} data-testid="radio-play-toggle"
                  className="mt-6 w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 flex items-center justify-center shadow-[0_10px_30px_rgba(239,68,68,0.4)] transition transform hover:scale-105 active:scale-95">
            {playing ? <Pause size={32} fill="white" className="text-white"/> : <Play size={32} fill="white" className="text-white ml-1"/>}
          </button>
          <p className="text-[11px] text-white/50 mt-2">
            {playing ? 'Streaming in corso...' : error ? `⚠️ ${error}` : 'Premi per iniziare l\'ascolto'}
          </p>

          {/* Volume */}
          <div className="w-full mt-6 flex items-center gap-3 max-w-xs">
            <Volume2 size={16} className="text-white/50 flex-shrink-0"/>
            <input type="range" min="0" max="1" step="0.01" value={volume}
                   onChange={e => setVolume(parseFloat(e.target.value))}
                   data-testid="radio-volume"
                   className="flex-1 accent-red-500 h-1"/>
            <span className="text-[10px] text-white/50 w-8 text-right">{Math.round(volume * 100)}%</span>
          </div>
        </div>

        {/* Palinsesto */}
        {schedule.length > 0 && (
          <div className="px-5 pb-5" data-testid="radio-schedule">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-2 flex items-center gap-2">
              <Radio size={12}/> A seguire
            </div>
            <div className="space-y-2">
              {schedule.map((s, i) => (
                <div key={`${s.id}-${i}`}
                     data-testid={`schedule-item-${i}`}
                     className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/10 p-2 hover:bg-white/10 transition">
                  <img src={resolveRadioImg(s.image)} alt=""
                       onError={(e) => { e.target.style.display = 'none'; }}
                       className="w-11 h-11 rounded-md object-cover flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{s.title}</div>
                    {s.staff && <div className="text-[10px] text-white/60 truncate">{s.staff.split('\n')[0].replace(/^Conduttori:\s*/, '')}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-white/70 font-mono">{s.start} — {s.end}</div>
                    <div className="text-[10px] text-white/40 uppercase">{s.is_today ? 'Oggi' : (s.is_tomorrow ? 'Domani' : s.day)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-black/50 flex justify-center">
          <a href={RADIO_WEBSITE} target="_blank" rel="noopener noreferrer"
             data-testid="radio-external-link"
             className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white">
            Sito ufficiale Radio Studio Più <ExternalLink size={12}/>
          </a>
        </div>
      </div>
    </div>
  );
}
